import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Bell, LogOut, Settings, User, Menu } from 'lucide-react';

export interface GlobalHeaderProps {
  tenant: {
    name: string;
    logo?: string;
    verse?: string;
    verseRef?: string;
  };
  user: {
    name: string;
    role: string;
    avatar?: string;
  };
  currentModule?: 'crm' | 'erp' | 'hrms' | 'finance' | 'lms' | 'portal';
  branch?: string;
  session?: string;
  onModuleSwitch?: (moduleId: string) => void;
  onBranchChange?: (branch: string) => void;
  onLogout?: () => void;
  onMenuClick?: () => void;
}

const MODULES = [
  { id: 'crm', label: 'CRM Engine', color: '#3B82F6' },
  { id: 'erp', label: 'ERP Core', color: '#10B981' },
  { id: 'finance', label: 'Finance OS', color: '#F43F5E' },
  { id: 'hrms', label: 'HRMS Portal', color: '#8B5CF6' },
  { id: 'lms', label: 'LMS System', color: '#F59E0B', soon: true },
];

const MODULE_COLORS: Record<string, string> = {
  crm: '#3B82F6',
  erp: '#10B981',
  finance: '#F43F5E',
  hrms: '#8B5CF6',
  lms: '#F59E0B',
};

const BRANCHES = ['All Branches', 'Kolkata', 'Mumbai', 'Dhanbad'];

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  tenant,
  user,
  currentModule,
  branch = 'All Branches',
  session = '2026-27',
  onModuleSwitch,
  onBranchChange,
  onLogout,
  onMenuClick,
}) => {
  const [moduleOpen, setModuleOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const moduleRef = useRef<HTMLDivElement>(null);
  const branchRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (moduleRef.current && !moduleRef.current.contains(e.target as Node)) setModuleOpen(false);
      if (branchRef.current && !branchRef.current.contains(e.target as Node)) setBranchOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentMod = MODULES.find(m => m.id === currentModule);
  const accentColor = (currentModule && MODULE_COLORS[currentModule]) || '#3B82F6';
  const initials = tenant.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  const userInitial = user.name.charAt(0).toUpperCase();


  return (
    <header
      style={{ borderBottom: `1px solid #e5e7eb`, borderBottomWidth: '1px' }}
      className="fixed top-0 left-0 right-0 h-[60px] bg-white z-50 flex items-stretch"
    >
      {/* Accent line at very top */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ backgroundColor: accentColor }}
      />

      {/* ══ LEFT: Tenant Identity ══ */}
      <div className="flex items-center gap-3 px-5 shrink-0">
        {/* Hamburger for mobile */}
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className="lg:hidden p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-50 mr-1"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        {/* School Logo */}
        <div
          onClick={() => {
            const isLocal = window.location.hostname === 'localhost';
            window.location.href = isLocal ? 'http://localhost:3000/?portal=true' : 'https://obis-edunexus-erp.web.app/?portal=true';
          }}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-sm cursor-pointer hover:opacity-80 transition-opacity"
          style={{ backgroundColor: accentColor }}
        >
          {tenant.logo ? (
            <img src={tenant.logo} alt="Logo" className="w-full h-full object-cover rounded-lg" />
          ) : (
            initials
          )}
        </div>

        {/* School Name + Module Switcher */}
        <div className="flex flex-col justify-center">
          <span className="text-[14px] font-bold text-gray-900 leading-tight tracking-tight">
            {tenant.name}
          </span>
          <div ref={moduleRef} className="relative flex items-center gap-1">
            <button 
              onClick={() => {
                const isLocal = window.location.hostname === 'localhost';
                window.location.href = isLocal ? 'http://localhost:3005' : 'https://obis-edunexus-portal.web.app';
              }}
              className="text-[11px] text-gray-400 font-bold hover:text-gray-600 transition-colors cursor-pointer"
            >
              COMMAND CENTER
            </button>
            <span className="text-gray-300 text-[11px]">•</span>
            <button
              onClick={() => setModuleOpen(v => !v)}
              className="flex items-center gap-0.5 text-[11px] font-bold hover:opacity-75 transition-opacity"
              style={{ color: accentColor }}
            >
              {currentMod ? currentMod.label : 'Switch Module'}
              <ChevronDown className="w-3 h-3" />
            </button>

            {moduleOpen && (
              <div className="absolute top-6 left-0 w-48 bg-white rounded-xl border border-gray-100 shadow-xl py-1.5 z-50">
                <div className="px-3 py-1.5 text-[9px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-50 mb-1">
                  Switch Module
                </div>
                {MODULES.map(mod => (
                  <button
                    key={mod.id}
                    onClick={() => {
                      setModuleOpen(false);
                      if (onModuleSwitch && mod.id !== currentModule) onModuleSwitch(mod.id);
                    }}
                    disabled={mod.soon}
                    className={`w-full flex items-center justify-between px-3 py-2 text-[12px] font-medium transition-colors ${
                      mod.soon
                        ? 'text-gray-300 cursor-not-allowed'
                        : mod.id === currentModule
                        ? 'bg-gray-50 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: mod.soon ? '#D1D5DB' : mod.color }} />
                      {mod.label}
                    </div>
                    {mod.soon && (
                      <span className="text-[9px] text-gray-300 italic font-medium">Soon</span>
                    )}
                    {!mod.soon && mod.id === currentModule && (
                      <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: accentColor }}>Active</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ CENTER: Spacer ══ */}
      <div className="hidden md:flex flex-1" />

      {/* ══ RIGHT: Operational Context ══ */}
      <div className="flex items-center gap-2 px-5 shrink-0">

        {/* Branch Selector */}
        <div ref={branchRef} className="relative hidden sm:block">
          <button
            onClick={() => setBranchOpen(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {branch}
            <ChevronDown className="w-3 h-3 text-gray-400" />
          </button>
          {branchOpen && (
            <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-xl border border-gray-100 shadow-xl py-1 z-50">
              {BRANCHES.map(b => (
                <button
                  key={b}
                  onClick={() => {
                    setBranchOpen(false);
                    if (onBranchChange) onBranchChange(b);
                  }}
                  className={`w-full text-left px-3 py-2 text-[12px] transition-colors ${
                    b === branch ? 'font-semibold text-gray-900 bg-gray-50' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Session Badge */}
        <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1.5 bg-green-50 border border-green-100 rounded-lg">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
          <span className="text-[11px] font-semibold text-green-700">Session: {session}</span>
        </div>

        {/* Notification Bell */}
        <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
          <Bell className="w-4.5 h-4.5" style={{ width: '18px', height: '18px' }} />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full border border-white" />
        </button>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-200 mx-1" />

        {/* User Profile */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen(v => !v)}
            className="flex items-center gap-2.5 hover:opacity-80 transition-opacity pl-1"
          >
            <div className="text-right hidden sm:block">
              <p className="text-[12px] font-bold text-gray-800 leading-tight">{user.name}</p>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider leading-tight">
                {user.role.replace('_', ' ')}
              </p>
            </div>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ring-2 ring-white shadow"
              style={{ backgroundColor: accentColor }}
            >
              {user.avatar ? (
                <img src={user.avatar} alt="Avatar" className="w-full h-full rounded-full object-cover" />
              ) : (
                userInitial
              )}
            </div>
          </button>

          {profileOpen && (
            <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-2xl border border-gray-100 shadow-xl py-2 z-50">
              <div className="px-4 py-3 border-b border-gray-50">
                <p className="text-[13px] font-bold text-gray-900">{user.name}</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">{user.role.replace('_', ' ')}</p>
              </div>
              <button className="w-full flex items-center gap-3 px-4 py-2 text-[12px] text-gray-600 hover:bg-gray-50 transition-colors">
                <User className="w-3.5 h-3.5" /> My Profile
              </button>
              <button className="w-full flex items-center gap-3 px-4 py-2 text-[12px] text-gray-600 hover:bg-gray-50 transition-colors">
                <Settings className="w-3.5 h-3.5" /> Settings
              </button>
              <div className="border-t border-gray-50 my-1" />
              <button
                onClick={() => { setProfileOpen(false); if (onLogout) onLogout(); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-[12px] text-red-500 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default GlobalHeader;
